import {
  addParam,
  errorCatch,
  getNotificationId,
  getTokenHeaders
} from '../helper';
import { getAxios } from '../axios';
import {
  ENDPOINT_ORGANIZATION_DETAILS,
  ENDPOINT_ORGANIZATIONS,
  ENDPOINT_ORGANIZATIONS_EXPORT,
  ENDPOINT_ORGANIZATIONS_EXPORT_OPTIONS,
  ENDPOINT_USER_ORGANIZATIONS
} from '../../config/endpoints';
import { Messages } from '../../config';

/**
 * Get list of all organizations
 * @param {object} payload
 * @param {boolean} [payload.disablePagination]
 * @returns {Promise<T>}
 */
export function getOrganizations(payload) {
  let headersAndParams = getTokenHeaders( payload.pagination ? payload.pagination : payload.disablePagination ? {} : { per_page: 15 } );
  return getAxios( false )
    .get(
      ENDPOINT_ORGANIZATIONS
      ,
      {
        headers: headersAndParams.headers,
        params: {
          ...headersAndParams.params,
          disablePagination: payload.disablePagination
        },
      }
    )
    .then( response => {
      return response && response.data ? response.data : null
    } )
    .catch(
      e => {
        errorCatch( e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        } );

        return e.response;
      }
    );
}

/**
 * Download organizations registered in the selected period as CSV.
 * Preset boundaries are calculated by the backend in Europe/Bucharest.
 *
 * @param {object} [payload]
 * @param {string} [payload.period]
 * @param {string} [payload.dateFrom]
 * @param {string} [payload.dateTo]
 * @param {'all'|'include'|'exclude'} [payload.organizationMode]
 * @param {string[]} [payload.organizationIds]
 * @returns {Promise<import('axios').AxiosResponse<Blob>>}
 */
export function exportOrganizations(payload = {}) {
  const period = payload.period || 'current_month';
  const organizationMode = ['include', 'exclude'].includes(payload.organizationMode)
    ? payload.organizationMode
    : 'all';
  const params = { period, organization_mode: organizationMode };

  if (period === 'custom') {
    params.date_from = payload.dateFrom;
    params.date_to = payload.dateTo;
  }

  if (organizationMode !== 'all' && Array.isArray(payload.organizationIds)) {
    const organizationIds = [...new Set(payload.organizationIds.filter(Boolean).map(String))];
    if (organizationIds.length > 0) {
      params.organization_ids = organizationIds;
    }
  }

  return getAxios().get(
    ENDPOINT_ORGANIZATIONS_EXPORT,
    {
      ...getTokenHeaders(),
      params,
      responseType: 'blob',
    }
  );
}

/**
 * Get the minimal organization catalog available to CSV export filters.
 *
 * @returns {Promise<{data: Array<{id: string, name: string}>}|import('axios').AxiosResponse>}
 */
export function getOrganizationExportOptions() {
  return getAxios(false)
    .get(ENDPOINT_ORGANIZATIONS_EXPORT_OPTIONS, getTokenHeaders())
    .then(response => response && response.data ? response.data : null)
    .catch(e => {
      errorCatch(e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      });

      return e.response;
    });
}

/**
 * Get details of organization
 * @param {object} payload
 * @param {string} payload.organizationId
 * @param {string} payload.notificationId
 * @param {string} payload.pagination
 * @param {string} payload.pagination.page_context
 * @returns {Promise<T>}
 */
export function getOrganizationDetails(payload) {
  return getAxios()
    .get(
      addParam( ENDPOINT_ORGANIZATION_DETAILS, {
        "{organization}": payload.organizationId,
      } ),
      getTokenHeaders(payload.pagination ? payload.pagination : {})
    )
    .then( (response) => {

      return response && response.data ? response.data : null;

    } )
    .catch( (e) => {
      errorCatch( e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      }, getNotificationId( payload ) );

      return e.response;
    } );
}

/**
 * Get list of user organizations
 * @param {object} payload
 * @param {string} payload.userId
 * @param {boolean} payload.hideNotification
 * @param {boolean} [payload.disablePagination]
 * @param {object} payload.pagination
 * @param {number} payload.pagination.page
 * @param {number} payload.pagination.per_page
 * @param {string} payload.pagination.sort_by
 * @param {string} payload.pagination.sort
 * @param {string} payload.pagination.q
 * @param {string} payload.pagination.page_context
 * @returns {Promise<T>}
 */
export function getUserOrganizations(payload) {
  if (payload && payload.disablePagination === true) {
    if (!payload.pagination) {
      payload.pagination = {};
    }

    payload.pagination.per_page = 100000;
  }

  return getAxios(payload.hideNotification).get(
    addParam(
      ENDPOINT_USER_ORGANIZATIONS,
      {
        '{user}': payload.userId,
      }
    ),
    getTokenHeaders(payload.pagination || null)
  )
    .then(response => {
      return response && response.data ? response.data : null
    })
    .catch(
      e => {
        errorCatch(e, {
          400: 'concat',
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        });

        return e.response;
      }
    );
}
